import { useSyncExternalStore } from 'react';
import { SIDEBAR_MODE_STORAGE_KEY } from '@/constants/localStorageKeys';

/**
 * `overlay` (default): collapses when the pointer leaves and floats over the page. `fixed`: always
 * expanded and takes layout space.
 */
export type SidebarMode = 'overlay' | 'fixed';

const readStoredMode = (): SidebarMode => {
  try {
    return window.localStorage.getItem(SIDEBAR_MODE_STORAGE_KEY) === 'fixed' ? 'fixed' : 'overlay';
  } catch {
    // Storage can be unavailable (private browsing) — fall back to the default.
    return 'overlay';
  }
};

/**
 * Module-level store because both the sidebar pin button and the settings hub write it. Read lazily
 * so tests that replace `localStorage` see their own value.
 */
let currentMode: SidebarMode | null = null;
const listeners = new Set<() => void>();

const getSidebarMode = (): SidebarMode => {
  if (currentMode === null) currentMode = readStoredMode();
  return currentMode;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setSidebarMode = (next: SidebarMode) => {
  if (getSidebarMode() === next) return;
  currentMode = next;

  try {
    window.localStorage.setItem(SIDEBAR_MODE_STORAGE_KEY, next);
  } catch {
    // Persistence is best-effort; the change still applies for this session.
  }

  listeners.forEach((listener) => listener());
};

export const useSidebarMode = (): SidebarMode => useSyncExternalStore(subscribe, getSidebarMode, getSidebarMode);
